<script setup lang="ts">
import {computed, inject, reactive, ref} from 'vue'
import type {usePrincipalStore} from '@/stores/iam/principal-store'
import {ImageScaler} from '@/utils/image-scaler'
import {PhoneInput} from "@hantera/design-system"
import {renderLanguage} from '@/constants/locales'
import {useLocalization} from '@hantera/portal-app'
import localization from '@/localization'

const store = inject<ReturnType<typeof usePrincipalStore>>('principalStore')!
const t = useLocalization('iam.principals')

// State
const isEditing = ref(false)
const saving = ref(false)
const uploadingPicture = ref(false)
const fileInput = ref<HTMLInputElement>()

// Edit form
const editForm = reactive({
  name: '',
  email: '',
  phone: '',
  language: 'en',
})

// Calculate initials from name or email
const initials = computed(() => {
  if (!store.principal) return '??'
  const name = store.principal.name
  if (name) {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
  const email = store.principal.email
  return email ? email.substring(0, 2).toUpperCase() : '??'
})

// Avatar style - show profile picture if available
const avatarStyle = computed(() => {
  const picture = store.principal?.picture
  if (picture) {
    return {
      backgroundImage: `url(${picture})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }
  return {}
})

// Format date
function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return t('time.justNow')
  if (diffMins < 60) {
    const plural = diffMins > 1 ? 's' : ''
    return t('time.minutesAgo', { count: diffMins, plural })
  }
  if (diffHours < 24) {
    const plural = diffHours > 1 ? 's' : ''
    return t('time.hoursAgo', { count: diffHours, plural })
  }
  if (diffDays < 7) {
    const plural = diffDays > 1 ? 's' : ''
    return t('time.daysAgo', { count: diffDays, plural })
  }

  return formatDateTime(d)
}

// Edit mode handlers
function startEditing() {
  if (!store.principal) return

  editForm.name = store.principal.name || ''
  editForm.email = store.principal.email || ''
  editForm.phone = store.principal.phone || ''
  editForm.language = store.principal.settings?.language || 'en'
  isEditing.value = true
}

function cancelEditing() {
  isEditing.value = false
  editForm.name = ''
  editForm.email = ''
  editForm.phone = ''
  editForm.language = 'en'
}

async function saveChanges() {
  if (!store.principal) return

  // Validate required fields
  if (!editForm.name?.trim()) {
    await store.dialogs.alert(t('dialogs.nameRequired'), t('dialogs.validationError'))
    return
  }

  if (!editForm.email?.trim()) {
    await store.dialogs.alert(t('dialogs.emailRequired'), t('dialogs.validationError'))
    return
  }

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(editForm.email)) {
    await store.dialogs.alert(t('dialogs.invalidEmail'), t('dialogs.validationError'))
    return
  }

  saving.value = true
  try {
    // Build settings object with language
    const settings = {
      ...(store.principal?.settings || {}),
      language: editForm.language
    }

    await store.updateProfile({
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      phone: editForm.phone?.trim() || undefined,
      settings
    })
    isEditing.value = false
  } catch (error) {
    // Error already handled by store
  } finally {
    saving.value = false
  }
}

// Picture management handlers
function triggerFileInput() {
  if (!store.canWrite || uploadingPicture.value) return
  fileInput.value?.click()
}

async function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !store.principal) return

  try {
    uploadingPicture.value = true
    const dataUrl = await ImageScaler.scaleAndEncode(file)

    await store.updateProfile({
      picture: dataUrl,
    })

    input.value = ''
  } catch (error: any) {
    await store.dialogs.alert(error.message, t('dialogs.uploadError'))
  } finally {
    uploadingPicture.value = false
  }
}

async function removePicture() {
  if (!store.principal || uploadingPicture.value) return

  const confirmed = await store.dialogs.confirm(
    t('dialogs.removePictureConfirm'),
    t('dialogs.confirmRemoval')
  )

  if (!confirmed) return

  try {
    uploadingPicture.value = true
    await store.updateProfile({
      picture: '',
    })
  } catch (error) {
    // Error already handled by store
  } finally {
    uploadingPicture.value = false
  }
}

// Action handlers
async function handleResetPassword() {
  if (!store.principal?.email) {
    await store.dialogs.alert(t('dialogs.cannotResetPassword'), 'Error')
    return
  }

  const sendEmail = await store.dialogs.confirm(
    t('dialogs.resetPasswordPrompt', { email: store.principal.email }),
    t('dialogs.resetPasswordTitle'),
    t('dialogs.emailToUser'),
    t('dialogs.showToMe'),
  )

  if (sendEmail === null) return

  try {
    const response = await store.iamService.resetPassword(store.principal.id, sendEmail as boolean)

    if (sendEmail) {
      const proc = store.processes.createProcess({
        title: t('dialogs.resetPasswordTitle'),
        description: t('dialogs.passwordResetSent', { email: store.principal.email }),
      })
      proc.complete()
    } else if (response.temporaryPassword) {
      await store.dialogs.alert(
        t('dialogs.temporaryPasswordMessage', { password: response.temporaryPassword }),
        t('dialogs.resetPasswordTitle'),
        'OK',
      )
    }

    await store.refresh()
  } catch (error: any) {
    console.error('Failed to reset password:', error)
    store.processes.error({
      message: error.response?.data?.message || 'Failed to reset password',
    })
  }
}

async function handleToggleSuspension() {
  if (!store.principal) return

  if (store.principal.suspendedAt) {
    await store.reactivate()
  } else {
    await store.suspend()
  }
}

async function handleDeleteUser() {
  if (!store.principal) return

  await store.deletePrincipal()
}
</script>

<template>
  <div v-if="store.principal" class="principal-sidebar">
    <div class="avatar-section">
      <!-- View Mode Avatar with Picture Management -->
      <div v-if="!isEditing" class="avatar-container">
        <div
          class="avatar-large"
          :class="{ clickable: store.canWrite && !uploadingPicture }"
          :style="avatarStyle"
          @click="triggerFileInput"
          :title="uploadingPicture ? t('avatar.processing') : store.canWrite ? t('avatar.clickToChange') : ''"
        >
          <span v-if="!store.principal.picture">{{ initials }}</span>
          <div v-if="store.canWrite" class="avatar-overlay">
            <span v-if="!uploadingPicture">{{ t('actions.change') }}</span>
            <span v-else>⏳</span>
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          @change="handleFileSelect"
          style="display: none"
        />
        <button
          v-if="store.canWrite && store.principal.picture"
          @click="removePicture"
          class="btn-silent"
          :disabled="uploadingPicture"
          type="button"
        >
          {{ t('actions.removePicture') }}
        </button>
      </div>

      <!-- Edit Mode Avatar (no picture management) -->
      <div v-else class="avatar-large" :style="avatarStyle">
        <span v-if="!store.principal.picture">{{ initials }}</span>
      </div>
    </div>

    <!-- View Mode -->
    <dl v-if="!isEditing" class="box-content">
      <template v-if="store.principal.name">
        <dt>{{ t('profile.name') }}</dt>
        <dd>{{ store.principal.name }}</dd>
      </template>
      <dt>{{ t('profile.email') }}</dt>
      <dd>{{ store.principal.email }}</dd>
      <template v-if="store.principal.phone">
        <dt>{{ t('profile.phone') }}</dt>
        <dd>{{ store.principal.phone }}</dd>
      </template>
      <dt>{{ t('profile.language') }}</dt>
      <dd>{{ renderLanguage(store.principal.settings?.language || 'en') }}</dd>
    </dl>
    
    <dl v-if="!isEditing" class="box-content">
      <dt>{{ t('profile.created') }}</dt>
      <dd>{{ formatDateTime(store.principal.createdAt) }}</dd>
      <dt>{{ t('profile.lastLogin') }}</dt>
      <dd>{{ store.principal.lastActiveAt ? formatRelativeTime(store.principal.lastActiveAt) : t('profile.never') }}</dd>
      <dt>{{ t('profile.principalId') }}</dt>
      <dd class="monospace">{{ store.principal.id }}</dd>
    </dl>

    <!-- Edit Mode -->
    <fieldset v-else class="box-content">
      <dl>
        <dt><label for="edit-name">{{ t('profile.name') }}</label></dt>
        <dd><input id="edit-name" type="text" v-model="editForm.name" :disabled="saving" /></dd>
        <dt><label for="edit-email">{{ t('profile.email') }}</label></dt>
        <dd><input id="edit-email" type="email" v-model="editForm.email" :disabled="saving" /></dd>
        <dt><label for="edit-phone">{{ t('profile.phone') }}</label></dt>
        <dd>
          <PhoneInput v-model="editForm.phone" :disabled="saving" id="edit-phone" />
        </dd>
        <dt><label for="edit-language">{{ t('profile.language') }}</label></dt>
        <dd>
          <select id="edit-language" v-model="editForm.language" :disabled="saving">
            <option v-for="language in Object.keys(localization)" :key="language" :value="language">
              {{ renderLanguage(language) }}
            </option>
          </select>
        </dd>
      </dl>
    </fieldset>

    <div v-if="store.canWrite" class="sidebar-actions">
      <template v-if="!isEditing">
        <button @click="startEditing">{{ t('actions.editProfile') }}</button>
        <button @click="handleResetPassword" class="btn-warning">{{ t('actions.resetPassword') }}</button>
        <button
          @click="handleToggleSuspension"
          :class="store.principal.suspendedAt ? 'btn-primary' : 'btn-danger'"
        >
          {{ store.principal.suspendedAt ? t('actions.reactivateUser') : t('actions.suspendUser') }}
        </button>
        <button @click="handleDeleteUser" class="btn-danger">
          {{ t('actions.deleteUser') }}
        </button>
      </template>
      <template v-else>
        <button @click="cancelEditing" class="btn-silent" :disabled="saving">
          {{ t('actions.cancel') }}
        </button>
        <button @click="saveChanges" class="btn-primary" :disabled="saving">
          {{ saving ? t('actions.saving') : t('actions.saveChanges') }}
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.principal-sidebar {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.avatar-section {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: hsl(var(--color-60));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  margin-bottom: 5px;
}

.avatar-large.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.avatar-large.clickable:hover {
  opacity: 0.9;
}

.avatar-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 12px;
  padding: 4px 4px 10px 4px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.avatar-large.clickable:hover .avatar-overlay {
  opacity: 1;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sidebar-info>div {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 0.5rem;
}

.monospace {
  font-family: monospace;
  font-size: 0.8125rem;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}
</style>
